import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  handleRequestError,
  Scan,
  scan as onScan,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
} from '@softline/application';
import { Store } from '@softline/core';
import { FieldOkComponent } from '@softline/dynamic';
import {
  ButtonComponent,
  EmptyStateComponent,
  IconComponent,
  InputComponent,
  MessageBarService,
  NumberInputComponent,
  SoftSlotDirective,
  SwitchComponent,
} from '@softline/ui-core';
import { OrderItem } from '../../models/order-item';
import { OrderTypeEnum } from '../../models/order-type-enum';
import { ItemScanRepository } from '../../repositories/item-scan.repository';
import { ItemTemplateRepository } from '../../repositories/item-template.repository';
import { ItemScanService } from '../../services/item-scan.service';
import { ItemTemplateService } from '../../services/item-template.service';
import { OrderService } from '../../services/order.service';

@Component({
  selector: 'soft-garage-order-items-form',
  templateUrl: './order-items-form.component.html',
  styleUrls: ['./order-items-form.component.css'],
  imports: [
    ReactiveFormsModule,
    InputComponent,
    IconComponent,
    NumberInputComponent,
    FieldOkComponent,
    DecimalPipe,
    ButtonComponent,
    SoftSlotDirective,
    EmptyStateComponent,
    SwitchComponent,
  ],
  providers: [
    ItemTemplateService,
    ItemTemplateRepository,
    ItemScanService,
    ItemScanRepository,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderItemsFormComponent {
  private readonly orderService = inject(OrderService);
  private readonly injector = inject(Injector);
  private readonly store = inject(Store);
  private readonly itemScanService = inject(ItemScanService);
  private readonly messageBarService = inject(MessageBarService);
  readonly itemTemplateService = inject(ItemTemplateService);
  readonly changePriceAndDiscount = input<boolean>(false);
  readonly orderType = input<OrderTypeEnum>(OrderTypeEnum.GARAGE);
  readonly autosave = input<boolean>(false);
  public readonly combineSameProduct = signal<boolean>(true);

  readonly currentItem = signal<any>(null);
  readonly currentQuantity = signal<number>(1);
  readonly currentPrice = signal<number | null>(null);
  readonly currentPricePerUnit = signal<number | null>(null);
  readonly currentDiscount = signal<number | null>(null);
  readonly currentLastEkPrice = signal<number | null>(null);

  readonly newItem = signal<Partial<OrderItem>>({});

  private readonly editingItemId = signal<number | null>(null);
  private readonly editingOrderId = signal<number | null>(null);
  private readonly editingSnapshot = signal<OrderItem | null>(null);

  public readonly isEditing = signal<boolean>(false);
  public readonly saving = signal<boolean>(false);

  public readonly items = model<OrderItem[]>([]);
  readonly scan = output<void>();

  readonly buttonVariant = computed<'primary' | 'secondary'>(() =>
    this.orderType() === OrderTypeEnum.PRODUCT ? 'secondary' : 'primary'
  );
  readonly accentColorStyle = computed(() => ({
    color: `var(${
      this.orderType() === OrderTypeEnum.PRODUCT ? '--colorSecondary' : '--colorPrimary'
    })`,
  }));

  readonly netTotal = computed(() =>
    this.items().reduce((sum, item) => sum + (item.price?.price ?? 0), 0)
  );

  constructor() {
    effect(async () => {
      const item = this.currentItem();
      if (!item) return;
      const quantity = this.currentQuantity();
      const currentDiscount = this.currentDiscount();
      const pricePerUnit = this.currentPricePerUnit();
      this.getTemplate(item, quantity, pricePerUnit, currentDiscount);
    });
    onScan((result) => this.resolveAndFill(result));
  }

  private async getTemplate(item, quantity, pricePerUnit?, currentDiscount?) {
    const isGarage = this.orderType() === OrderTypeEnum.GARAGE;
    const orderId = isGarage
      ? this.orderService.garageOrder()?.id
      : this.orderService.productOrder()?.id;
    const body = {
      orderId,
      product: item,
      quantity: quantity,
    };
    if (currentDiscount) {
      body['discount'] = currentDiscount;
    }
    if (pricePerUnit) {
      body['price'] = {};
      body['price']['pricePerUnit'] = pricePerUnit;
    }
    const itemTemplate = isGarage
      ? await this.orderService.getGarageOrderItemTemplate(body)
      : await this.orderService.getProductOrderItemTemplate(body);
    this.currentPrice.set(itemTemplate.price.price);
    this.currentLastEkPrice.set(itemTemplate?.lastEkPrice);
    if (this.currentPricePerUnit() == null) {
      this.currentPricePerUnit.set(itemTemplate.price.pricePerUnit);
    }
    if (this.currentDiscount() == null) {
      this.currentDiscount.set(itemTemplate?.discount);
    }
    this.newItem.set(itemTemplate);
    return itemTemplate;
  }

  public readonly addItem = async () => {
    if (this.saving()) return;
    this.saving.set(true);
    try {
      const item = this.currentItem();
      let quantity = this.currentQuantity();
      const currentDiscount = this.currentDiscount();
      const pricePerUnit = this.currentPricePerUnit();
      const itemTemplate = await this.getTemplate(
        item,
        quantity,
        pricePerUnit,
        currentDiscount
      );
      if (!itemTemplate) return;

      const isGarage = this.orderType() === OrderTypeEnum.GARAGE;
      let editingItemId = this.editingItemId();
      let editingOrderId = this.editingOrderId();

      if (editingItemId == null && this.combineSameProduct()) {
        const existing = this.items().find(
          (i) => i.product?.id === itemTemplate.product?.id
        );
        if (existing) {
          editingItemId = existing.id ?? null;
          editingOrderId = existing.orderId ?? null;
          quantity = existing.quantity + quantity;
          const merged = await this.getTemplate(
            item,
            quantity,
            pricePerUnit,
            currentDiscount
          );
          if (merged) Object.assign(itemTemplate, merged);
          this.items.set(this.items().filter((i) => i.id !== existing.id));
        }
      }

      const orderId =
        editingOrderId ??
        (isGarage
          ? this.orderService.garageOrder()?.id
          : this.orderService.productOrder()?.id) ??
        null;
      const newEntry: OrderItem = {
        ...itemTemplate,
        quantity,
        ...(editingItemId != null ? { id: editingItemId } : {}),
        ...(orderId != null ? { orderId } : {}),
      };
      const previousItems = this.items();

      this.items.update((list) => [...list, newEntry]);

      if (this.autosave()) {
        try {
          if (editingItemId != null) {
            if (isGarage) {
              await this.orderService.updateGarageOrderItem(editingItemId, newEntry);
            } else {
              await this.orderService.updateProductOrderItem(editingItemId, newEntry);
            }
          } else {
            if (isGarage) {
              await this.orderService.createGarageOrderItem([newEntry]);
            } else {
              await this.orderService.createProductOrderItem([newEntry]);
            }
          }
          this.items.set(
            isGarage
              ? this.orderService.garageOrderItems()
              : this.orderService.productOrderItems()
          );
        } catch (e) {
          this.items.set(previousItems);
          handleRequestError(e, this.injector, { show: true });
          return;
        }
      }

      this.resetForm();
    } finally {
      this.saving.set(false);
    }
  };

  private resetForm(): void {
    this.currentItem.set(null);
    this.currentQuantity.set(1);
    this.currentPrice.set(null);
    this.currentPricePerUnit.set(null);
    this.currentDiscount.set(null);
    this.currentLastEkPrice.set(null);
    this.editingItemId.set(null);
    this.editingOrderId.set(null);
    this.editingSnapshot.set(null);
    this.isEditing.set(false);
  }

  public readonly cancelEdit = () => {
    const snapshot = this.editingSnapshot();
    if (snapshot && !this.items().some((i) => i.id === snapshot.id)) {
      this.items.update((list) => [...list, snapshot]);
    }
    this.resetForm();
  };

  public readonly editItem = (item: OrderItem) => {
    const previousSnapshot = this.editingSnapshot();
    let list = this.items();
    if (previousSnapshot && !list.some((i) => i.id === previousSnapshot.id)) {
      list = [...list, previousSnapshot];
    }
    this.isEditing.set(true);
    this.editingSnapshot.set(item);
    this.editingItemId.set(item.id ?? null);
    this.editingOrderId.set(item.orderId ?? null);
    this.currentItem.set({
      id: item.product.id,
      cartikelnummer: item.product.number,
      matchcode: item.product.matchcode,
      artbez1: item.product.name,
    });
    this.currentQuantity.set(item.quantity);
    this.currentPrice.set(item.price.price);
    this.currentPricePerUnit.set(item.price.pricePerUnit);
    this.currentDiscount.set(item.discount);
    this.currentLastEkPrice.set(item.lastEkPrice);
    this.items.set(list.filter((i) => i.id !== item.id));
  };

  public readonly removeItem = async (item: OrderItem) => {
    if (item.id != null) {
      try {
        if (this.orderType() === OrderTypeEnum.GARAGE) {
          await this.orderService.deleteGarageOrderItem(item);
          this.items.set(this.orderService.garageOrderItems());
        } else {
          await this.orderService.deleteProductOrderItem(item);
          this.items.set(this.orderService.productOrderItems());
        }
      } catch {
        return;
      }
      return;
    }
    this.items.set(this.items().filter((i) => i.id !== item.id));
  };

  public readonly setProduct = (product: {
    id: number;
    number?: string;
    name?: string;
    matchcode?: string;
  }): void => {
    this.isEditing.set(false);
    this.editingItemId.set(null);
    this.editingOrderId.set(null);
    this.editingSnapshot.set(null);
    this.currentItem.set({
      id: product.id,
      cartikelnummer: product.number,
      matchcode: product.matchcode,
      artbez1: product.name,
    });
    this.currentQuantity.set(1);
  };

  public readonly startScan = async (): Promise<void> => {
    this.scan.emit();
    const result = (await this.store.dispatch(
      SOFTLINE_FEATURE_SCANNER,
      ScannerStore.actions.scan,
      {}
    )) as Scan | undefined;
    await this.resolveAndFill(result);
  };

  private readonly resolveAndFill = async (result: Scan | undefined): Promise<void> => {
    if (!result?.data) return;
    try {
      const product = await this.itemScanService.resolveProduct(
        result.labelType ?? 'unknown',
        result.data
      );
      if (!product) {
        this.messageBarService.error('Kein Produkt zum gescannten Code gefunden.');
        return;
      }
      this.setProduct(product);
      await this.addItem();
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  };

  readonly negativeColor = (value: number | null): string | null =>
    value != null && value < 0 ? 'var(--ds-text-danger)' : null;
}
